import { ConnectionStatusMode } from '@/authenticated/components/DeviceConnectionRedirect'
import { useUrlSearchParam } from '@/hooks'
import {
  DeviceAgnosticFleetAuthentication,
  ScopedFleetAuthentication,
  TeslaFleetAuthenticationScope,
} from '@/types/teslaFleetAuthentication'
import { useEnergySystemFleetAuthUrl } from '../../connect-device/energy-system/select/tesla/permissions-statement/hooks'
import { useEffect, useState } from 'react'
import {
  deviceAgnosticFleetAuthenticationCollection,
  useFetchEnergySystemRequiredScopes,
} from '@/reducers/teslaFleetAuthentication'
import useApiDispatch from '@/hooks/useApiDispatch/useApiDispatch'
import { useNavigation } from '@/app/hooks'
import { energySystemCollection } from '@/reducers/energySystems'
import { EnergySystem } from '@/types/energySystem'

const copy = {
  statusText: {
    connecting: 'Hold tight...we’re finishing up your connection',
    connected: 'Great! You successfully connected your vehicle',
    failed: 'Oh no! Something went wrong. Please go back and try again',
  },
  cancelledLoginErrorText:
    'Looks like you cancelled the login process. Please try again.',
}

function useCreateFleetAuthenticationFromCodeInUrl() {
  const { apiDispatch } = useApiDispatch()
  const [isLoadingOrIdle, setIsLoadingOrIdle] = useState(true)
  const [missingPermissions, setMissingPermissions] = useState(false)
  const [authenticatedEnergySystem, setAuthenticatedEnergySystem] =
    useState<ScopedFleetAuthentication | null>(null)
  const [initializedEnergySystems, setInitializedEnergySystems] = useState<
    EnergySystem[]
  >([])
  const { createAction: createFleetAuthAction, error: fleetAuthError } =
    deviceAgnosticFleetAuthenticationCollection.useCreate()
  const {
    createAction: initializeEnergySystemAction,
    error: initializeEnergySystemError,
  } = energySystemCollection.useCreate({
    path: 'energy-systems/tesla/initialize',
  })

  async function createFleetAuthentication(code: string) {
    setIsLoadingOrIdle(true)
    const res = await apiDispatch<DeviceAgnosticFleetAuthentication>(
      createFleetAuthAction({
        code,
        device_type: 'energy_system',
      }),
    )
    if (res.ok) {
      setAuthenticatedEnergySystem(res.data)
      const initEnergySystemRes = await apiDispatch<EnergySystem[]>(
        initializeEnergySystemAction({
          id: res.data.id,
        }),
      )
      if (initEnergySystemRes.ok) {
        setInitializedEnergySystems(initEnergySystemRes.data)
      } else {
        const status = initEnergySystemRes.error.status
        // 403 indicates the user doesn't have sufficient permissions
        if (status === 403) {
          setMissingPermissions(true)
        }
      }
    }
    setIsLoadingOrIdle(false)
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    if (code) {
      createFleetAuthentication(code)
    }
  }, [])

  // if the user doesn't have sufficient permissions, dont show an error, instead allow the user
  // to be redirected to the no system information page
  const errorInitializingEnergySystem = missingPermissions
    ? null
    : initializeEnergySystemError

  const error = fleetAuthError ?? errorInitializingEnergySystem

  return {
    isLoadingOrIdle,
    error,
    authenticatedEnergySystem,
    initializedEnergySystems,
    missingPermissions,
  }
}

function getConnectionStatusMode(
  userCancelledTeslaLogin: boolean,
  tokenLoading: boolean,
  tokenError: boolean,
) {
  if (userCancelledTeslaLogin) {
    return ConnectionStatusMode.Failed
  }

  if (tokenLoading) {
    return ConnectionStatusMode.Connecting
  } else if (tokenError) {
    return ConnectionStatusMode.Failed
  }
  return ConnectionStatusMode.Connected
}

function getMissingScopes(
  authenticatedEnergySystem: ScopedFleetAuthentication | null,
  requiredEnergyScopes: TeslaFleetAuthenticationScope[],
) {
  if (authenticatedEnergySystem === null) {
    return requiredEnergyScopes
  }

  const missingScopes = requiredEnergyScopes.filter(
    (scope) => !authenticatedEnergySystem.scopes.includes(scope),
  )
  return missingScopes
}

export function useViewModel() {
  const navigation = useNavigation()

  // fetch the fleet authorize url so that if the user cancels the login from the
  // tesla login page, we can redirect them back to the fleet authorize url
  const fleetAuthorizeUrl = useEnergySystemFleetAuthUrl()

  // todo turn required energy scopes into a collection and fetch here
  const { data: requiredEnergyScopes } = useFetchEnergySystemRequiredScopes()

  // create the fleet authentication from url on page load
  const {
    isLoadingOrIdle: tokenLoading,
    error: tokenError,
    authenticatedEnergySystem,
    initializedEnergySystems,
    missingPermissions,
  } = useCreateFleetAuthenticationFromCodeInUrl()

  const userCancelledTeslaLogin =
    useUrlSearchParam('error') === 'login_cancelled'

  const missingScopes: TeslaFleetAuthenticationScope[] = getMissingScopes(
    authenticatedEnergySystem,
    requiredEnergyScopes,
  )

  const connectionStatusMode = getConnectionStatusMode(
    userCancelledTeslaLogin,
    tokenLoading,
    Boolean(tokenError),
  )

  const buttonAction = () => {
    // If the user cancelled the login, redirect them back to the fleet authorize url to retry
    if (userCancelledTeslaLogin && fleetAuthorizeUrl) {
      window.location.href = fleetAuthorizeUrl
      return
    }

    // if the user authenticated an energy system, redirect them to the energy system device page
    if (
      connectionStatusMode === ConnectionStatusMode.Connected &&
      authenticatedEnergySystem !== null
    ) {
      return navigation.push(
        `/devices/energy-system/${authenticatedEnergySystem.id}`,
      )
    }
    navigation.push('/app')
  }

  const statusText = copy.statusText

  const alertText = {
    failed: userCancelledTeslaLogin ? copy.cancelledLoginErrorText : undefined,
  }

  return {
    mode: connectionStatusMode,
    statusText,
    alertText,
    buttonAction,
    // todo determine do we need this? I think missing scopes is a deprecated property of the
    // device connection redirect component
    // missingScopes,
    authenticatedEnergySystem,
    initializedEnergySystems,
    missingPermissions,
  }
}

export default useViewModel
